import React, { createContext, useContext, useState, useMemo } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

const ThemeModeContext = createContext({ mode: 'light', toggleMode: () => {} });

export function useThemeMode() {
  return useContext(ThemeModeContext);
}

function buildTheme(mode) {
  const dark = mode === 'dark';
  return createTheme({
    palette: {
      mode,
      primary:   { main: '#6366f1', dark: '#4f46e5', light: '#818cf8', contrastText: '#fff' },
      secondary: { main: '#8b5cf6' },
      success:   { main: '#16a34a', light: '#bbf7d0', contrastText: '#fff' },
      error:     { main: '#dc2626', light: '#fee2e2', contrastText: '#fff' },
      warning:   { main: '#d97706', light: '#fef3c7', contrastText: '#fff' },
      info:      { main: '#0284c7', light: '#e0f2fe', contrastText: '#fff' },
      background: {
        default: dark ? '#0f172a' : '#f9fafb',
        paper:   dark ? '#1e293b' : '#ffffff',
      },
      text: {
        primary:   dark ? '#f1f5f9' : '#111827',
        secondary: dark ? '#94a3b8' : '#6b7280',
        disabled:  dark ? '#475569' : '#9ca3af',
      },
      divider: dark ? 'rgba(148,163,184,0.12)' : 'rgba(0,0,0,0.08)',
      action: {
        hover:    dark ? 'rgba(99,102,241,0.08)'  : 'rgba(99,102,241,0.06)',
        selected: dark ? 'rgba(99,102,241,0.15)'  : 'rgba(99,102,241,0.08)',
        focus:    dark ? 'rgba(99,102,241,0.12)'  : 'rgba(99,102,241,0.08)',
        active:   dark ? 'rgba(241,245,249,0.54)' : 'rgba(0,0,0,0.54)',
      },
    },
    shape: { borderRadius: 8 },
    shadows: [
      'none',
      '0px 1px 2px rgba(0,0,0,0.05)',
      '0px 1px 3px rgba(0,0,0,0.08), 0px 1px 2px rgba(0,0,0,0.04)',
      '0px 4px 6px -1px rgba(0,0,0,0.07), 0px 2px 4px -1px rgba(0,0,0,0.04)',
      '0px 10px 15px -3px rgba(0,0,0,0.08), 0px 4px 6px -2px rgba(0,0,0,0.03)',
      '0px 20px 25px -5px rgba(0,0,0,0.08), 0px 10px 10px -5px rgba(0,0,0,0.02)',
      ...Array(19).fill('none'),
    ],
    typography: {
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      fontSize: 14,
      h1: { fontWeight: 800, letterSpacing: '-0.025em' },
      h2: { fontWeight: 800, letterSpacing: '-0.025em' },
      h3: { fontWeight: 700, letterSpacing: '-0.02em' },
      h4: { fontWeight: 700, letterSpacing: '-0.015em' },
      h5: { fontWeight: 700, letterSpacing: '-0.01em' },
      h6: { fontWeight: 700 },
      subtitle1: { fontWeight: 600 },
      subtitle2: { fontWeight: 600 },
      body1: { fontSize: '0.9375rem', lineHeight: 1.6 },
      body2: { fontSize: '0.875rem', lineHeight: 1.5 },
      caption: { fontSize: '0.75rem', fontWeight: 500 },
      button: { textTransform: 'none', fontWeight: 600, letterSpacing: '0.01em' },
      overline: { textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.08em', fontSize: '0.6875rem' },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          '*, *::before, *::after': { boxSizing: 'border-box' },
          body: {
            scrollbarWidth: 'thin',
            scrollbarColor: dark ? '#334155 transparent' : '#d1d5db transparent',
            '&::-webkit-scrollbar': { width: 6, height: 6 },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': {
              background: dark ? '#334155' : '#d1d5db',
              borderRadius: 3,
              '&:hover': { background: dark ? '#475569' : '#9ca3af' },
            },
          },
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            border: `1px solid ${dark ? 'rgba(148,163,184,0.12)' : 'rgba(0,0,0,0.08)'}`,
          },
          elevation1: {
            boxShadow: '0px 1px 3px rgba(0,0,0,0.08), 0px 1px 2px rgba(0,0,0,0.04)',
            border: 'none',
          },
          elevation2: {
            boxShadow: '0px 4px 6px -1px rgba(0,0,0,0.07), 0px 2px 4px -1px rgba(0,0,0,0.04)',
            border: 'none',
          },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            border: `1px solid ${dark ? 'rgba(148,163,184,0.12)' : 'rgba(0,0,0,0.08)'}`,
            transition: 'box-shadow 0.15s ease, transform 0.15s ease',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: { root: { backgroundImage: 'none' } },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: { borderRadius: 8, overflow: 'hidden' },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-head': {
              backgroundColor: dark ? 'rgba(15,23,42,0.6)' : '#f9fafb',
              color: dark ? '#94a3b8' : '#6b7280',
              fontWeight: 600,
              fontSize: 11,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              padding: '10px 16px',
              borderBottom: `1px solid ${dark ? 'rgba(148,163,184,0.12)' : 'rgba(0,0,0,0.08)'}`,
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            padding: '12px 16px',
            borderBottom: `1px solid ${dark ? 'rgba(148,163,184,0.08)' : '#f3f4f6'}`,
            fontSize: 13.5,
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:hover:not(.MuiTableRow-head)': {
              backgroundColor: dark ? 'rgba(99,102,241,0.04)' : '#fafafa',
            },
            '&:last-child .MuiTableCell-root': { borderBottom: 'none' },
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: 7,
            fontWeight: 600,
            fontSize: 13.5,
            padding: '6px 14px',
            transition: 'all 0.15s ease',
          },
          contained: {
            boxShadow: 'none',
            '&:hover': { boxShadow: '0 1px 4px rgba(0,0,0,0.15)', transform: 'translateY(-0.5px)' },
          },
          outlined: {
            borderColor: dark ? 'rgba(148,163,184,0.25)' : 'rgba(0,0,0,0.15)',
            '&:hover': {
              borderColor: '#6366f1',
              backgroundColor: 'rgba(99,102,241,0.04)',
            },
          },
          text: {
            '&:hover': { backgroundColor: dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' },
          },
          sizeSmall: { fontSize: 12.5, padding: '4px 10px' },
          sizeLarge: { fontSize: 15, padding: '10px 22px' },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 7,
            transition: 'all 0.15s ease',
            '&:hover': { backgroundColor: dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)' },
          },
        },
      },
      MuiTextField: {
        defaultProps: { size: 'small' },
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 7,
              fontSize: 13.5,
              '& fieldset': { borderColor: dark ? 'rgba(148,163,184,0.2)' : 'rgba(0,0,0,0.14)' },
              '&:hover fieldset': { borderColor: dark ? 'rgba(148,163,184,0.35)' : 'rgba(0,0,0,0.28)' },
              '&.Mui-focused fieldset': { borderColor: '#6366f1', borderWidth: 1.5 },
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            borderRadius: 7,
            fontSize: 13.5,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontWeight: 600,
            fontSize: 12,
            borderRadius: 6,
          },
          sizeSmall: { height: 22, fontSize: 11 },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundImage: 'none',
            backgroundColor: dark ? '#1e293b' : '#ffffff',
            border: 'none',
            borderRadius: 12,
            boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: { fontWeight: 700, fontSize: 16, padding: '20px 24px 12px' },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: { padding: '8px 24px' },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: { padding: '16px 24px 20px', gap: 8 },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundImage: 'none',
            backgroundColor: dark ? '#1e293b' : '#ffffff',
            border: `1px solid ${dark ? 'rgba(148,163,184,0.15)' : 'rgba(0,0,0,0.09)'}`,
            borderRadius: 10,
            boxShadow: '0 10px 25px rgba(0,0,0,0.12)',
            marginTop: 4,
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontSize: 13.5,
            borderRadius: 6,
            margin: '1px 6px',
            padding: '7px 12px',
            minWidth: 'calc(100% - 12px)',
            '&:hover': { backgroundColor: dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' },
            '&.Mui-selected': {
              backgroundColor: dark ? 'rgba(99,102,241,0.15)' : 'rgba(99,102,241,0.08)',
              color: '#6366f1',
              '&:hover': { backgroundColor: dark ? 'rgba(99,102,241,0.2)' : 'rgba(99,102,241,0.12)' },
            },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: dark ? '#0f172a' : '#1f2937',
            color: '#f9fafb',
            fontSize: 12,
            fontWeight: 500,
            borderRadius: 6,
            padding: '5px 10px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          },
          arrow: { color: dark ? '#0f172a' : '#1f2937' },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: { borderColor: dark ? 'rgba(148,163,184,0.1)' : 'rgba(0,0,0,0.07)' },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 7,
            '&.Mui-selected': {
              backgroundColor: dark ? 'rgba(99,102,241,0.15)' : 'rgba(99,102,241,0.08)',
              '&:hover': { backgroundColor: dark ? 'rgba(99,102,241,0.2)' : 'rgba(99,102,241,0.12)' },
            },
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: { padding: 6 },
          thumb: { boxShadow: '0 1px 4px rgba(0,0,0,0.2)' },
          track: {
            borderRadius: 10,
            opacity: 1,
            backgroundColor: dark ? '#334155' : '#d1d5db',
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            fontSize: 13.5,
            minHeight: 40,
            padding: '8px 16px',
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: { height: 2, borderRadius: 2 },
          root: { minHeight: 40 },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: { borderRadius: 4, height: 6 },
          bar: { borderRadius: 4 },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: { fontWeight: 700 },
        },
      },
      MuiBadge: {
        styleOverrides: {
          badge: { fontWeight: 700, fontSize: 10 },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: 8, fontSize: 13.5 },
          standardSuccess: { backgroundColor: dark ? 'rgba(22,163,74,0.1)' : '#f0fdf4', color: dark ? '#86efac' : '#15803d' },
          standardError:   { backgroundColor: dark ? 'rgba(220,38,38,0.1)' : '#fef2f2', color: dark ? '#fca5a5' : '#b91c1c' },
          standardWarning: { backgroundColor: dark ? 'rgba(217,119,6,0.1)' : '#fffbeb', color: dark ? '#fcd34d' : '#92400e' },
          standardInfo:    { backgroundColor: dark ? 'rgba(2,132,199,0.1)'  : '#f0f9ff', color: dark ? '#7dd3fc'  : '#075985' },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            fontSize: 13,
            border: `1px solid ${dark ? 'rgba(148,163,184,0.2)' : 'rgba(0,0,0,0.12)'}`,
            borderRadius: '7px !important',
            '&.Mui-selected': {
              backgroundColor: 'rgba(99,102,241,0.1)',
              color: '#6366f1',
              borderColor: 'rgba(99,102,241,0.4)',
              '&:hover': { backgroundColor: 'rgba(99,102,241,0.15)' },
            },
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: { fontSize: 13.5 },
        },
      },
    },
  });
}

export function ThemeModeProvider({ children }) {
  const stored = localStorage.getItem('mtaani_theme') || 'light';
  const [mode, setMode] = useState(stored === 'dark' ? 'dark' : 'light');

  const toggleMode = () => {
    setMode((prev) => {
      const next = prev === 'light' ? 'dark' : 'light';
      localStorage.setItem('mtaani_theme', next);
      return next;
    });
  };

  const theme = useMemo(() => buildTheme(mode), [mode]);

  return (
    <ThemeModeContext.Provider value={{ mode, toggleMode }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
}
